import { getBookDetail } from 'api/libros';
import { getDetailProduct } from 'api/products';

/** Valores enviados al backend y guardados en cada línea del carrito */
export const BOOK_CONDITION = {
  NEW: 'new',
  USED: 'used',
  /** "Como nuevo" — parámetro API: `newbalance`. */
  NEWBALANCE: 'newbalance',
};

/** True si `value` coincide con un valor de condición guardado en carrito/pedido. */
export function isKnownStoredBookCondition(value) {
  return Object.values(BOOK_CONDITION).includes(value);
}

/** Incluye casos donde el API del carrito trae condición pero omite `type` o flags vienen aparte. */
export function isBookProduct(product) {
  if (!product || typeof product !== 'object') return false;
  const t = product.type;
  if (typeof t === 'string') {
    const n = t.toLowerCase();
    if (n === 'libro' || n === 'book') return true;
  }
  if (t === 'libro' || t === 'book') return true;
  if (product.has_used != null || product.has_newbalance != null) return true;
  if (isKnownStoredBookCondition(product.book_condition)) return true;
  return false;
}

/** El usuario debe elegir condición si el libro ofrece usado y/o como nuevo además del nuevo */
export function productNeedsConditionChoice(product) {
  if (!isBookProduct(product)) return false;
  return !!(product.has_used || product.has_newbalance);
}

/** True si falta algún flag para armar las mismas opciones que el modal en listados/detalle (p. ej. carrito sin has_stock). */
export function bookConditionFlagsMissing(product) {
  if (!isBookProduct(product)) return false;
  if (product.has_used == null || product.has_newbalance == null) return true;
  const offersAlt = !!(product.has_used || product.has_newbalance);
  if (offersAlt && product.has_stock == null) return true;
  return false;
}

/** Completa has_stock/has_used/has_newbalance desde el detalle (carrito, listados con campos reducidos, etc.) */
export async function hydrateBookConditionFlags(product) {
  if (!isBookProduct(product) || !bookConditionFlagsMissing(product) || product?.id == null) {
    return product;
  }
  try {
    let status;
    let data;
    if (product.type === 'book') {
      ({ status, data } = await getDetailProduct(product.id));
    } else {
      ({ status, data } = await getBookDetail(product.id));
    }
    if (status === 200 && data && typeof data === 'object') {
      return {
        ...product,
        has_stock: data.has_stock ?? product.has_stock,
        has_used: data.has_used ?? product.has_used,
        has_newbalance: data.has_newbalance ?? product.has_newbalance,
      };
    }
  } catch (e) {
    console.error('hydrateBookConditionFlags', e);
  }
  return product;
}

export function cartLinesMatch(lineA, lineB) {
  if (!lineA || !lineB) return false;
  if (lineA.id !== lineB.id) return false;
  return (lineA.book_condition ?? null) === (lineB.book_condition ?? null);
}

/** Condición en líneas de carrito o pedido (API: snake/camel; producto vs product). */
export function getBookConditionFromOrderLine(line) {
  if (!line || typeof line !== 'object') return undefined;
  const v =
    line.book_condition ??
    line.bookCondition ??
    line.product?.book_condition ??
    line.product?.bookCondition ??
    line.producto?.book_condition ??
    line.producto?.bookCondition;
  if (v === null || v === undefined || v === '') return undefined;
  return v;
}

/** Línea de pedido (p. ej. `articles` o `items` del detalle): si el artículo se trata como libro. */
export function isBookOrderLine(line) {
  if (!line || typeof line !== 'object') return false;
  if (isBookProduct(line.product) || isBookProduct(line.producto)) return true;
  const t = line.type ?? line.product_type;
  if (typeof t === 'string') {
    const n = t.toLowerCase();
    if (n === 'libro' || n === 'book') return true;
  }
  if (t === 'libro' || t === 'book') return true;
  if (isKnownStoredBookCondition(getBookConditionFromOrderLine(line))) return true;
  return false;
}

/** Clave i18n (namespace `detalle`) para la cabecera de columna según el tipo de líneas. */
export function orderLinesConditionColumnI18nKey(lines) {
  if (!lines?.length) return 'bookConditionColumn';
  const flags = lines.map((ln) => isBookOrderLine(ln));
  if (flags.every(Boolean)) return 'bookConditionColumn';
  if (flags.every((f) => !f)) return 'productConditionColumn';
  return 'itemConditionShortColumn';
}

/** Alias: líneas del carrito devueltas por el API. */
export function getBookConditionFromCartApiLine(line) {
  return getBookConditionFromOrderLine(line);
}

/** Clave i18n en namespace `detalle` (bookConditionNew, etc.). */
export function bookConditionTranslationKey(storedValue) {
  if (storedValue == null || storedValue === '') return null;
  const v = String(storedValue).toLowerCase();
  if (v === BOOK_CONDITION.NEW) return 'bookConditionNew';
  if (v === BOOK_CONDITION.USED) return 'bookConditionUsed';
  if (v === BOOK_CONDITION.NEWBALANCE) return 'bookConditionLikeNew';
  return null;
}

/** Texto para mostrar condición/estado en líneas de pedido (solo lectura). Si el EP no envía dato, se muestra «Nuevo». */
export function formatOrderLineBookConditionDisplay(line, translateDetalle) {
  const raw = getBookConditionFromOrderLine(line);
  const key = bookConditionTranslationKey(raw);
  if (key) return translateDetalle(key);
  if (raw != null && String(raw).trim() !== '') return String(raw);
  return translateDetalle('bookConditionNew');
}

export function buildAddItemCartPayload(itemId, quantity, product, bookCondition) {
  const payload = { item: itemId, quantity: quantity ?? 1 };
  const cond = bookCondition ?? product?.book_condition;
  if (productNeedsConditionChoice(product) || cond) {
    payload.book_condition = cond || BOOK_CONDITION.NEW;
  }
  return payload;
}

export function normalizeAddItemCartResponse(data) {
  if (!data || typeof data !== 'object') return data;
  if (data.unit_price != null) return data;
  if (data.data && typeof data.data === 'object' && data.data.unit_price != null) return data.data;
  if (data.item && typeof data.item === 'object' && data.item.unit_price != null) return data.item;
  return data;
}

/** Para decrease_quantity y remove_item cuando el backend distingue líneas por condición */
export function buildCartLineMutationPayload(cartLine) {
  const payload = { item: cartLine.id };
  if (cartLine.book_condition) {
    payload.book_condition = cartLine.book_condition;
  }
  return payload;
}
